import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Droplet, FileText, Map, Phone, AlertCircle, Calendar, ShieldCheck, Download } from 'lucide-react';
import { IMAGES, CONTACT_INFO, EMERGENCY_CONTACT, DOCUMENTS } from '../data';

export default function Home() {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [showAllAnnouncements, setShowAllAnnouncements] = useState(false);
  const [showAllShortcuts, setShowAllShortcuts] = useState(false);

  useEffect(() => {
    // Fetch announcements from public CMS endpoint
    fetch('https://api.dev.auxo.dk/public/content/announcements')
      .then((res) => {
        if (!res.ok) throw new Error('Failed to fetch');
        return res.json();
      })
      .then((data) => {
        // Sort by date or ID to get the latest
        setAnnouncements(data || []);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Error fetching announcements:', err);
        setError(true);
        setLoading(false);
      });
  }, []);

  // Fallback announcements if API fails
  const fallbackAnnouncements = [
    {
      id: '1',
      title: { da: 'Nye Vandure' },
      date: { da: '12. maj 2026' },
      content: { da: '<div class="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-6 items-center"><div><p>Vandværket vil begynde installation af nye vandure den 26/5 2026.</p><p>Installationen forventes at løbe over de næste 2 måneder.</p><p>Der vil tilgå hver enkelt forbruger meddelelse om nøjagtig tidspunkt for installation, samtidigt mulighed for downloading af APP fra <a href="https://klimaid.dk/" target="_blank" rel="noopener noreferrer">KlimaID</a> som vil give hver enkelt forbruger mulighed for at følge sit vandforbrug, og modtage arlarmer ved brud på instalationen.</p></div><div class="flex justify-center md:justify-end"><img src="https://assets.auxodev.dk/cdn-cgi/image/width=400,quality=85,format=auto/assets/74070791-98c6-4435-9dcf-71cab0b4761c/image.png" alt="KlimaID" class="rounded-xl border border-slate-200 shadow-md w-full max-w-[170px]" /></div></div>' },
      important: false,
    },
    {
      id: '2',
      title: { da: 'Generalforsamling 2026 afholdt' },
      date: { da: '27. februar 2026' },
      content: { da: '<p>Den årlige generalforsamling blev afholdt torsdag den 26. februar 2026 i Grindsted Forsamlingshus.</p><p>Referat og formandens beretning er nu tilgængelige under mødereferater.</p>' },
      important: false,
    },
    {
      id: '3',
      title: { da: 'Planlagt ledningsarbejde på Smedevej' },
      date: { da: '15. april 2026' },
      content: { da: '<p>I forbindelse med renovering af hovedledningen vil der være kortvarige afbrydelser af vandforsyningen på Smedevej tirsdag den 21. april mellem kl. 09:00 og 12:00.</p>' },
      important: true,
    }
  ];

  const displayAnnouncements = error || announcements.length === 0 ? fallbackAnnouncements : announcements;
  const importantAnnouncement = displayAnnouncements.find(a => a.important);
  const visibleAnnouncements = showAllAnnouncements ? displayAnnouncements : displayAnnouncements.slice(0, 1);

  const shortcuts = [
    {
      id: 'vandkvalitet',
      title: 'Vandkvalitet',
      description: 'Se de seneste vandanalyser og målinger for hårdhed, kalk og mineraler.',
      link: '/information',
      linkLabel: 'Læs mere',
      icon: Droplet,
      isDarkBlue: true,
    },
    {
      id: 'forsyningsomraade',
      title: 'Forsyningsområde',
      description: 'Se kortet over vores forsyningsområde og find ud af, om du kan få vand fra os.',
      link: '/information',
      linkLabel: 'Se kort',
      icon: Map,
    },
    {
      id: 'moedereferater',
      title: 'Mødereferater',
      description: 'Læs referater fra generalforsamlinger, formandens beretning og årsrapporter.',
      link: '/moedereferater',
      linkLabel: 'Se referater',
      icon: FileText,
    },
    {
      id: 'kontakt',
      title: 'Kontakt & Bestyrelse',
      description: 'Find kontaktoplysninger på bestyrelsen, forretningsføreren og vores kontortider.',
      link: '/kontakt',
      linkLabel: 'Se kontakt',
      icon: Phone,
    },
  ];

  const visibleShortcuts = showAllShortcuts ? shortcuts : shortcuts.slice(0, 2);

  return (
    <div className="bg-slate-50">
      {/* Important Alert Banner */}
      {importantAnnouncement && (
        <div className="bg-amber-500 text-slate-950 py-3.5 px-4 shadow-inner">
          <div className="mx-auto max-w-7xl flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-2.5">
              <AlertCircle className="h-5 w-5 shrink-0 animate-bounce" />
              <span className="text-sm font-bold">
                Vigtig meddelelse: {importantAnnouncement.title.da} ({importantAnnouncement.date.da})
              </span>
            </div>
            <Link
              to="/meddelelser"
              className="inline-flex items-center gap-1 text-xs font-bold text-slate-950 hover:underline transition"
            >
              Læs mere
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <div className="relative overflow-hidden bg-slate-950 py-24 text-white lg:py-32">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/assets/74070791-98c6-4435-9dcf-71cab0b4761c/f845d549-6f18-4d5c-bca3-fd96ef449f16.jpg"
            alt="Grindsted Vandværk baggrund"
            className="h-full w-full object-cover object-center opacity-90"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-blue-950 via-blue-950/90 via-blue-950/60 to-transparent" />
        </div>

        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="mt-6 text-4xl font-bold tracking-tight text-white sm:text-5xl lg:text-6xl">
              Velkommen til Grindsted Vandværk
            </h1>
            <p className="mt-6 text-lg text-white/90 leading-relaxed max-w-2xl">
              Vi forsyner vores forbrugere med rent, velsmagende og kontrolleret drikkevand. Her på siden finder du information om takster, analyser, vedtægter, generalforsamlinger samt mulighed for at melde flytning.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/meddel-flytning"
                className="inline-flex items-center gap-2 rounded-full bg-blue-600 px-8 py-3.5 text-sm font-bold text-white hover:bg-blue-500 hover:-translate-y-1 transition-all duration-300 transform"
              >
                Meld Flytning
                <ArrowRight className="h-4 w-4 text-white" />
              </Link>
              <Link
                to="/information"
                className="inline-flex items-center gap-2 rounded-full border border-white bg-transparent px-8 py-3.5 text-sm font-bold text-white hover:bg-white/10 hover:-translate-y-1 hover:shadow-lg hover:shadow-white/5 transition-all duration-300 transform"
              >
                Se Vandkvalitet
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Shortcuts Grid (MOVED HERE: Under Hero) */}
      <div className="bg-white border-y border-slate-200/60 overflow-hidden">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:items-center">
            {/* Left Column: Shortcuts Grid (Now on the left, col-6) */}
            <div className="lg:col-span-6">
              <div className="grid grid-cols-1 gap-6">
                {visibleShortcuts.map((shortcut) => {
                  const IconComponent = shortcut.icon;
                  if (shortcut.isDarkBlue) {
                    return (
                      <div
                        key={shortcut.id}
                        className="relative group rounded-2xl border border-blue-950 p-6 bg-blue-950 text-white shadow-md hover:shadow-xl hover:bg-slate-900 hover:border-slate-900 transition-all duration-300"
                      >
                        <div className="flex h-12 w-12 items-center justify-start text-white transition-all">
                          <IconComponent className="h-8 w-8" />
                        </div>
                        <h3 className="mt-4 text-lg font-bold text-white">{shortcut.title}</h3>
                        <p className="mt-2 text-sm text-white leading-relaxed">
                          {shortcut.description}
                        </p>
                        <Link
                          to={shortcut.link}
                          className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-white hover:text-blue-200 hover:underline"
                        >
                          {shortcut.linkLabel} <ArrowRight className="h-3.5 w-3.5" />
                        </Link>
                      </div>
                    );
                  }

                  return (
                    <div
                      key={shortcut.id}
                      className="relative group rounded-2xl border border-slate-100 p-6 bg-white shadow-md hover:shadow-xl hover:border-slate-200 transition-all duration-300"
                    >
                      <div className="flex h-12 w-12 items-center justify-start text-black transition-all">
                        <IconComponent className="h-8 w-8" />
                      </div>
                      <h3 className="mt-4 text-lg font-bold text-slate-900">{shortcut.title}</h3>
                      <p className="mt-2 text-sm text-slate-500 leading-relaxed">
                        {shortcut.description}
                      </p>
                      <Link
                        to={shortcut.link}
                        className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-black hover:underline"
                      >
                        {shortcut.linkLabel} <ArrowRight className="h-3.5 w-3.5" />
                      </Link>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Right Column: Blue Half with Watermark in Color (Now on the right, col-6) */}
            <div className="lg:col-span-6 relative">
              {/* Colored Watermark */}
              <div className="absolute right-0 -bottom-16 text-blue-600/10 pointer-events-none select-none z-0">
                <Droplet className="h-72 w-72 stroke-[1] fill-blue-600/5" />
              </div>
              
              <div className="relative z-10">
                <h2 className="text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
                  Hvad leder du efter?
                </h2>
                <p className="mt-4 text-slate-600 leading-relaxed max-w-xl">
                  Find hurtigt de mest efterspurgte informationer, dokumenter og selvbetjeningsløsninger.
                </p>
                
                <div className="mt-8">
                  <button
                    onClick={() => setShowAllShortcuts(!showAllShortcuts)}
                    className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-6 py-2.5 text-sm font-bold text-white hover:bg-slate-800 hover:-translate-y-0.5 hover:shadow-md transition-all duration-300 transform cursor-pointer"
                  >
                    {showAllShortcuts ? 'Vis færre genveje' : 'Udvid til flere genveje'}
                    <ArrowRight className={`h-4 w-4 transform transition-transform ${showAllShortcuts ? '-rotate-90' : 'rotate-90'}`} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* News & Waterworks Image Section */}
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:items-start">
          {/* Left: Waterworks Image */}
          <div className="lg:col-span-6 lg:order-last">
            <div className="relative overflow-hidden bg-slate-100 shadow-xl shadow-slate-200/50">
              <img
                src={IMAGES.waterworksSecondary}
                alt="Grindsted Vandværk bygning"
                className="h-[320px] w-full object-cover object-center transition-transform duration-500 hover:scale-102"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-blue-950/80 via-blue-950/50 to-transparent pointer-events-none" />
            </div>

            {/* Partner Logos */}
            <div className="mt-8 border-t border-slate-200/60 pt-6">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-4">
                Vi samarbejder med og overvåges af
              </p>
              <div className="flex flex-wrap items-center gap-6 sm:gap-8">
                <img src={IMAGES.logos.danskeVandvaerker} alt="Danske Vandværker" className="h-8 object-contain opacity-80 hover:opacity-100 transition" />
                <img src={IMAGES.logos.geus} alt="GEUS" className="h-6 object-contain opacity-80 hover:opacity-100 transition" />
                <img src={IMAGES.logos.aalborgKommune} alt="Aalborg Kommune" className="h-8 object-contain opacity-80 hover:opacity-100 transition" />
                <img src={IMAGES.logos.miljoestyrelsen} alt="Miljøstyrelsen" className="h-8 object-contain opacity-80 hover:opacity-100 transition" />
              </div>
            </div>
          </div>

          {/* Right: News/Announcements list */}
          <div className="lg:col-span-6 space-y-8 lg:order-first">
            <div>
              <h2 className="text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
                Seneste Meddelelser
              </h2>
              <p className="mt-3 text-slate-500">
                Hold dig opdateret med de seneste nyheder, driftsforstyrrelser og informationer fra vandværket.
              </p>
            </div>

            <div className="space-y-4">
              {loading ? (
                <div className="space-y-4">
                  {[1, 2].map((i) => (
                    <div key={i} className="animate-pulse rounded-2xl border border-slate-200 bg-white p-6">
                      <div className="h-4 w-1/4 rounded bg-slate-200" />
                      <div className="mt-4 h-6 w-3/4 rounded bg-slate-200" />
                      <div className="mt-2 h-4 w-5/6 rounded bg-slate-200" />
                    </div>
                  ))}
                </div>
              ) : (
                visibleAnnouncements.map((item) => (
                  <div
                    key={item.id}
                    className={`relative rounded-2xl border p-6 transition-all duration-200 bg-white shadow-sm hover:shadow-md ${
                      item.important
                        ? 'border-black bg-amber-50/30'
                        : 'border-slate-200/80'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-4">
                      <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500">
                        <Calendar className="h-3.5 w-3.5 text-black" />
                        {item.date.da}
                      </span>
                    </div>
                    <h3 className="mt-3 text-lg font-bold text-slate-900 group-hover:text-blue-600">
                      {item.title.da}
                    </h3>
                    <div
                      className="mt-3 text-sm text-slate-600 leading-relaxed prose prose-sm max-w-none flow-root [&_a]:text-blue-600 [&_a]:underline [&_a]:font-bold hover:[&_a]:text-blue-800 transition-colors"
                      dangerouslySetInnerHTML={{ __html: item.content.da }}
                    />
                  </div>
                ))
              )}
            </div>

            <div className="pt-2 flex flex-wrap items-center gap-4">
              {displayAnnouncements.length > 1 && (
                <button
                  onClick={() => setShowAllAnnouncements(!showAllAnnouncements)}
                  className="inline-flex items-center gap-2 rounded-full bg-blue-600 px-6 py-2.5 text-sm font-bold text-white hover:bg-blue-500 hover:-translate-y-0.5 hover:shadow-md transition-all duration-300 transform cursor-pointer"
                >
                  {showAllAnnouncements ? 'Vis færre meddelelser' : 'Vis flere meddelelser'}
                  <ArrowRight className={`h-4 w-4 transform transition-transform ${showAllAnnouncements ? '-rotate-90' : 'rotate-90'}`} />
                </button>
              )}
              <Link
                to="/meddelelser"
                className="inline-flex items-center gap-2 font-bold text-slate-600 hover:text-blue-600 transition text-sm py-2.5"
              >
                Se alle meddelelser på egen side
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
